import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import { Button, Badge } from 'react-bootstrap';
import './TicketTable.css';

const TicketTable = ({ data, handleEdit }) => {
  const [activeTab, setActiveTab] = useState('All');
  const [searchText, setSearchText] = useState('');

  // Filter data based on active tab
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Filter by status tab
    if (activeTab === 'Open') {
      filtered = filtered.filter(item =>
        item.status.toLowerCase() === 'open' ||
        item.status.toLowerCase() === 'pending' ||
        item.status.toLowerCase() === 'in process'
      );
    } else if (activeTab === 'Closed') {
      filtered = filtered.filter(item =>
        item.status.toLowerCase() === 'closed' ||
        item.status.toLowerCase() === 'resolved'
      );
    } else if (activeTab === 'reopen') {
      filtered = filtered.filter(item =>
        item.status.toLowerCase() === 'reopen'
      );
    }

    // Filter by search text
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item => {
        return (
          item.id.toString().includes(searchLower) ||
          item.owner.toLowerCase().includes(searchLower) ||
          item.models.some(model => model.name.toLowerCase().includes(searchLower)) ||
          item.areas.some(area => area.name.toLowerCase().includes(searchLower)) ||
          item.sub_areas.some(subarea => subarea.name.toLowerCase().includes(searchLower)) ||
          (item.query && item.query.toLowerCase().includes(searchLower))
        );
      });
    }

    // Sort by ID in descending order
    return filtered.sort((a, b) => b.id - a.id);
  }, [data, activeTab, searchText]);

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        borderRadius: '8px',
        overflow: 'hidden',
        width: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f0f7ff', // Lighter blue background for header
        borderBottom: '1px solid #e9ecef',
        fontWeight: 'bold',
        color: '#175FA4',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        fontSize: '14px',
        '&:hover': {
          backgroundColor: '#f8f9fa',
          cursor: 'pointer',
        },
        borderBottom: '1px solid #f0f0f0',
      },
      stripedStyle: {
        backgroundColor: '#fafbff', // Very light blue for striped rows
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #e9ecef',
        padding: '16px 0',
      },
    },
  };

  // Define columns for DataTable
  const columns = [
    {
      name: 'Ticket ID',
      selector: row => row.id,
      sortable: true,
      width: '120px',
      reorder: true,
    },
    {
      name: 'Generated By',
      selector: row => row.owner,
      sortable: true,
      reorder: true,
    },
    {
      name: 'Issue Date',
      selector: row => row.ticketDate,
      sortable: true,
      reorder: true,
    },
    // {
    //   name: 'Module',
    //   cell: row => (
    //     <div className="module-badges">
    //       {row.models.slice(0, 2).map((model) => (
    //         <Badge
    //           key={model.id}
    //           bg="info"
    //           className="module-badge"
    //           style={{
    //             marginRight: '4px',
    //             marginBottom: '4px',
    //             backgroundColor: '#E9F6FC',
    //             color: '#1E84B0',
    //             fontWeight: 'normal',
    //             padding: '5px 8px',
    //             borderRadius: '4px'
    //           }}
    //         >
    //           {model.name}
    //         </Badge>
    //       ))}
    //       {row.models.length > 2 && (
    //         <Badge
    //           bg="secondary"
    //           className="module-badge-count"
    //           style={{
    //             backgroundColor: '#f0f0f0',
    //             color: '#666',
    //             fontWeight: 'normal',
    //             padding: '5px 8px',
    //             borderRadius: '4px'
    //           }}
    //         >
    //           +{row.models.length - 2}
    //         </Badge>
    //       )}
    //     </div>
    //   ),
    //   reorder: true,
    // },
     {
      name: 'Module',
      cell: row => (
        <div>
          {row.models && row.models.length > 0 ? (
            row.models.slice(0, 2).map((mod) => (
              <span
                key={mod.id}
                className='p-1 mx-1 moduleSubHeading'
                style={{
                  background: '#E9F6FC',
                  borderRadius: '4px',
                  color: '#1E84B0',
                  display: 'inline-block',
                  marginBottom: '2px',
                  fontSize: '12px'
                }}
              >
                {mod.name}
              </span>
            ))
          ) : (
            <span>-</span>
          )}
          {row.models && row.models.length > 2 && (
            <span
              className='p-1 mx-1'
              style={{
                background: '#f0f0f0',
                borderRadius: '4px',
                color: '#666',
                display: 'inline-block',
                marginBottom: '2px',
                fontSize: '12px'
              }}
            >
              +{row.models.length - 2} more
            </span>
          )}
        </div>
      ),
    },
    {
      name: 'Status',
      cell: row => {
        const status = row.status.toLowerCase();
        let bgColor = '#e9ecef';
        let textColor = '#666';

        if (status === 'open') {
          bgColor = '#fff'; 
          textColor = '#fff'; 
        } else if (status === 'closed') {
          bgColor = '#7fbf7f'; // Light green
          textColor = '#fff'; // Green
        }else if (status === 'reopen') {
          bgColor = '#7fbf7f'; // Light green
          textColor = '#fff'; // Green
        }

        return (
          <Badge
            className="status-badge"
            style={{
              backgroundColor: bgColor,
              color: textColor,
              fontWeight: 'normal',
              padding: '6px 10px',
              borderRadius: '4px',
              fontSize: '12px'
            }}
          >
            {row.status}
          </Badge>
        );
      },
      sortable: true,
      reorder: true,
    },
    // {
    //   name: 'Priority',
    //   cell: row => {
    //     const priority = row.priority.toLowerCase();
    //     let bgColor = '#e3f2fd'; // Light blue for low
    //     let textColor = '#1976d2'; // Blue for low

    //     if (priority === 'high') {
    //       bgColor = '#ffebee'; // Light red
    //       textColor = '#d32f2f'; // Red
    //     } else if (priority === 'medium') {
    //       bgColor = '#fff8e1'; // Light yellow
    //       textColor = '#ffa000'; // Amber
    //     }

    //     return (
    //       <Badge
    //         className="priority-badge"
    //         style={{
    //           backgroundColor: bgColor,
    //           color: textColor,
    //           fontWeight: 'normal',
    //           padding: '6px 10px',
    //           borderRadius: '4px',
    //           fontSize: '12px'
    //         }}
    //       >
    //         {row.priority}
    //       </Badge>
    //     );
    //   },
    //   sortable: true,
    //   reorder: true,
    // },
    {
      name: 'Priority',
      selector: row => row.priority,
      sortable: true,
      reorder: true,
    },
    {
      name: 'Actions',
      cell: row => {
        const status = row.status.toLowerCase();
        const isClosed = status === 'closed' || status === 'resolved';

        return (
          <Button
            variant={isClosed ? 'warning' : 'primary'}
            size="sm"
            style={{
              backgroundColor: isClosed ? '#ffa000' : '#1976d2',
              borderColor: isClosed ? '#ff8f00' : '#1565c0',
              borderRadius: '4px',
              fontWeight: 'normal',
              padding: '6px 12px',
              boxShadow: 'none'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row, 'reopen');
            }}
          >
            {isClosed ? 'Reopen' : 'Update'}
          </Button>
        );
      },
      button: true,
      reorder: true,
    },
  ];

  return (
    <div className="ticket-table-container">
      <div className="ticket-table-header">
        <div className="ticket-tabs">
          <button
            className={`ticket-tab ${activeTab === 'All' ? 'active' : ''}`}
            onClick={() => setActiveTab('All')}
          >
            All
          </button>
          <button
            className={`ticket-tab ${activeTab === 'Open' ? 'active' : ''}`}
            onClick={() => setActiveTab('Open')}
          >
            Open
          </button>
          <button
            className={`ticket-tab ${activeTab === 'Closed' ? 'active' : ''}`}
            onClick={() => setActiveTab('Closed')}
          >
            Closed
          </button>
          <button
            className={`ticket-tab ${activeTab === 'reopen' ? 'active' : ''}`}
            onClick={() => setActiveTab('reopen')}
          >
            Reopen
          </button>
        </div>
        <div className="ticket-search">
          <input
            type="text"
            placeholder="Search tickets..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="ticket-search-input"
          />
          <span className="search-icon">
            <i className="fa fa-search" style={{ color: '#aaa' }}></i>
          </span>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50]}
        customStyles={customStyles}
        onRowClicked={(row) => handleEdit(row)}
        pointerOnHover
        highlightOnHover
        striped
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        noDataComponent={
          <div className="no-data-message">
            <p>No tickets found</p>
          </div>
        }
      />
    </div>
  );
};

export default TicketTable;
